export type Block =
  | { t: 'h2'; x: string }
  | { t: 'p'; x: string }
  | { t: 'ul'; items: string[] }
  | { t: 'link'; href: string; label: string }

export type Post = {
  slug: string
  title: string
  /** ISO date, used for machine-readable markup and sorting. */
  date: string
  dateLabel: string
  author: string
  category: string
  hero?: string
  /** 4:3 crop used when the post leads the Signals index. Falls back to hero. */
  card?: string
  excerpt: string
  body: Block[]
  /** Kept routable for URL parity, but hidden from the index. */
  draft?: boolean
}

const HERO_WHISPER = 'https://cdn.magicpatterns.com/uploads/q7ebhZ77URnMQesakhicK1/whisper-hero-wide.jpg'
const CARD_WHISPER = 'https://cdn.magicpatterns.com/uploads/3Dqx1WXDnTEdhMm8iGF2AG/whisper-featured-4x3.jpg'
const HERO_JANUARY = 'https://cdn.magicpatterns.com/uploads/gnmtoZZjMCCoVf52R2HHfV/pasted-image.webp'
const HERO_TIME = 'https://cdn.magicpatterns.com/uploads/1GwGtAGkzN5yHByQpCP4jv/pasted-image.webp'
const HERO_AGENTS = 'https://cdn.magicpatterns.com/uploads/9DDaBnxm9Dz7Xm8b9ZniaH/photo-1485827404703-89b55fcc595e.jpg'
const HERO_FRONTIER = 'https://cdn.magicpatterns.com/uploads/sKmwGjc7C4WZ1bPrnztjNA/photo-1558494949-ef010cbdcc31.jpg'
const HERO_ACCESS = 'https://cdn.magicpatterns.com/uploads/d2apcmYAQ1sFmkULE6zf6Y/photo-1559757148-5c350d0d3c56.jpg'
const HERO_EIGHT = 'https://cdn.magicpatterns.com/uploads/m8By8VPsiumrikiai6W95J/photo-1526374965328-7f61d4dc18c5.jpg'
const HERO_GRAPHS = 'https://cdn.magicpatterns.com/uploads/gWs8FdcqbFYvUkMsVzsvRr/Screenshot_2026-01-03_at_11.00.52_PM.png'
const HERO_EXEC = 'https://cdn.magicpatterns.com/uploads/s9T3KsdJbV3SgdX8uqaQdU/photo-1639322537228-f710d846310a.jpg'
const HERO_PAPER = 'https://cdn.magicpatterns.com/uploads/crwx3NrDSUkjuwZRe9HAJG/photo-1451187580459-43490279c0fa.jpg'

const WHISPER: Block[] = [
  { t: 'h2', x: 'What Are You Whispering to the Machine?' },
  { t: 'p', x: 'Before an AI system answers a question, it sits idle. Cold. Dark. Silent.' },
  {
    t: 'p',
    x: 'There is no reasoning happening yet. No tokens being spent. No intelligence at work. Just a model waiting for a signal strong enough to wake it up. Then a question arrives. The moment the input lands, everything changes. The system lights up. Compute spins. Tokens flow. The model heats up and starts doing what it was designed to do: predict, reason, generate.',
  },
  { t: 'p', x: 'That instant (before the first token is produced) is where outcomes are decided.' },
  { t: 'p', x: 'It is also the moment most enterprises leave to chance.' },
  {
    t: 'p',
    x: 'Today, almost all guidance to a model arrives in the same form: a prompt. A sentence or two describing what the user wants. Maybe a reminder about tone. Sometimes a warning about compliance or audience. The prompt is the whisper that wakes the machine and tells it what matters.',
  },
  {
    t: 'p',
    x: 'And prompts matter. They shape direction and intent. But they are almost always incomplete.',
  },
  {
    t: 'p',
    x: 'A prompt can suggest what’s important, but it cannot carry everything an enterprise knows. It does not encode institutional standards. It does not reliably express brand boundaries. It does not understand role responsibility, regulatory posture, or situational risk unless those things are restated every single time. Most importantly, it does not persist.',
  },
  {
    t: 'p',
    x: 'Enterprises keep trying to whisper louder by writing longer prompts, stacking reminders, or hoping better phrasing will magically produce better behavior. But the problem isn’t volume. It’s structure. What enterprises actually want to whisper to the machine is far richer than a single instruction.',
  },
  {
    t: 'p',
    x: 'They want to whisper how their brand speaks and what it never says. They want to whisper what a compliance officer cares about, what a marketer must avoid, what a clinician is responsible for, and what a financial advisor is obligated to disclose. They want to whisper how a person should act in their role — not just what kind of answer to produce.',
  },
  { t: 'p', x: 'They want the model to behave, not just respond.' },
  {
    t: 'p',
    x: 'This is where whispering becomes real. Ex: Governance. Governance is not a document or a policy PDF. It is the quiet guidance that shapes behavior before action occurs. In human systems, that guidance comes from culture, training, and experience. In AI systems, it must be delivered deliberately, every time, before computation begins. It’s vertical knowledge that needs to be delivered by contextual intelligence.',
  },
  {
    t: 'p',
    x: 'Right now, most enterprises are trying to deliver all of that through a single prompt and hoping the model fills in the gaps.',
  },
  { t: 'p', x: 'It won’t. Or it will, incorrectly.' },
  {
    t: 'p',
    x: 'Models don’t misbehave because they are reckless. They misbehave because they were never told what mattered with enough clarity or consistency. When context is missing, they improvise. When constraints are vague, they generalize. When role expectations are absent, they default to generic behavior.',
  },
  {
    t: 'p',
    x: 'This is why prompted systems feel fragile. They work until they don’t. They succeed in low-stakes situations and fail precisely where enterprises care the most. If X = Y, sure, go for it. Is that business?',
  },
  { t: 'p', x: 'With Synthia, the whisper happens before the prompt ever reaches the model.' },
  {
    t: 'p',
    x: 'Instead of relying on a single instruction to carry everything, Synthia assembles structured instruction packages, pre-flight. These packages contain the full set of signals an enterprise wants the model to hear before it starts reasoning: role expectations, brand standards, compliance logic, behavioral constraints, and situational context. The prompt still matters, but it no longer carries the burden alone.',
  },
  { t: 'p', x: 'The machine wakes up informed.' },
  {
    t: 'p',
    x: 'It knows who is asking. It knows how it is allowed to behave. It knows what matters, what is risky, and what is off-limits. It doesn’t have to guess. It doesn’t have to improvise. It doesn’t have to infer intent from a single sentence.',
  },
  { t: 'p', x: 'That changes everything.' },
  {
    t: 'p',
    x: 'As AI moves deeper into regulated workflows, customer-facing systems, and decision support, the most important moment is no longer the answer. It is the instant before the answer exists. The moment when the model transitions from silence to computation. That is where trust is either established or lost.',
  },
  {
    t: 'p',
    x: 'So the real question enterprises should be asking isn’t whether they are prompting well. It is what they are whispering to the machine before it starts to think — and whether that whisper truly reflects how their organization expects decisions to be made.',
  },
  { t: 'p', x: 'Because once the tokens start flowing, it is already too late to explain what you meant.' },
]

const JANUARY: Block[] = [
  {
    t: 'p',
    x: 'In January 2026, regulators made something unmistakably clear: the next phase of AI adoption is not about better models or more impressive demos. It is about governance that exists before AI acts.',
  },
  {
    t: 'p',
    x: 'This is not a theoretical shift. It is a regulatory pivot toward executable expectations. Enterprises are now expected to demonstrate documented intent, traceability, security controls, and auditability before an AI system produces a single token.',
  },
  { t: 'h2', x: 'Three regulatory signals define this moment.' },
  {
    t: 'p',
    x: 'The FDA and the EMA jointly released ten Guiding Principles of Good AI Practice in Drug Development on January 14, 2026. These principles are not aspirational. They explicitly state that AI systems used in regulated decision-making must have a clearly defined context of use, risk-based controls, lifecycle documentation, and governance spanning design, validation, performance, and ongoing monitoring. In short, regulators now expect enterprises to know — and prove — what AI is allowed to do in a given situation and why.',
  },
  {
    t: 'p',
    x: 'At the same time, the NIST published the preliminary draft of its Cybersecurity Framework Profile for Artificial Intelligence for public comment through late January. This draft overlays AI-specific expectations onto CSF 2.0, treating AI systems as first-class citizens of cybersecurity governance. Identity, access control, telemetry, supply-chain transparency, and model and data provenance are no longer optional considerations. They are quickly becoming the baseline for what regulators consider reasonable AI maturity.',
  },
  {
    t: 'p',
    x: 'In financial services, the FINRA reinforced the same message in its 2026 Annual Oversight Report. Existing obligations around supervision, recordkeeping, communications capture, and fair dealing apply fully to generative AI. Firms are expected to log, monitor, and supervise AI-driven outputs the same way they would any regulated system or communication. AI does not receive a special exemption. It must fit inside established compliance frameworks.',
  },
  { t: 'p', x: 'The common thread across all three signals is simple and consequential.' },
  {
    t: 'h2',
    x: 'Enterprises must be able to prove intent, constraints, and governance before AI produces output.',
  },
  {
    t: 'p',
    x: 'Regulators are no longer satisfied with policy documents, slide decks, or model cards created after the fact. They want evidence that:',
  },
  {
    t: 'ul',
    items: [
      'Context of use was defined',
      'Constraints were applied',
      'Controls existed at the moment generation occurred',
    ],
  },
  { t: 'p', x: 'This is where Synthia fundamentally changes the conversation.' },
  {
    t: 'p',
    x: 'Synthia assembles instruction packets pre-flight, before any model generates an answer. These packets encode role expectations, brand standards, compliance logic, and situational constraints in a structured, repeatable way. That moves enterprises from saying “we help with guardrails” to something far more powerful: “we generate the evidence regulators expect.”',
  },
  { t: 'p', x: 'This is not an incremental improvement. It is a category upgrade.' },
  {
    t: 'p',
    x: 'The FDA and EMA’s emphasis on defined context of use and lifecycle governance means enterprises must be able to answer a very specific question every time AI is used: what was this model allowed to do in this situation?',
  },
  {
    t: 'p',
    x: 'With Synthia, role, brand, and compliance packets are no longer optional layers added by careful teams. They become the mechanism for answering that question consistently. Without a packet, there is no defensible audit trail.',
  },
  {
    t: 'p',
    x: 'What regulators are implicitly asking for maps cleanly to what Synthia already produces:',
  },
  {
    t: 'ul',
    items: [
      'Context of use → packet metadata',
      'Lifecycle traceability → packet versioning',
      'Cyber controls → packet composition and source integrity',
      'Supervision → packet-linked logs',
    ],
  },
  { t: 'h2', x: 'Synthia does not just govern AI behavior. It emits proof.' },
  {
    t: 'p',
    x: 'This reframing matters deeply for buyers. Governance stops being an abstract control function and becomes a concrete artifact that can be inspected, logged, and defended.',
  },
  {
    t: 'p',
    x: 'Just as importantly, this regulatory shift avoids a trap many enterprises fear: model-level compliance. Regulators are not mandating specific models. They are not blessing particular vendors. They are mandating governance behavior.',
  },
  {
    t: 'p',
    x: 'Because Synthia sits above models, governance remains stable even as models change. OpenAI today. Internal models tomorrow. Agentic workflows next year. The control layer stays consistent while the underlying technology evolves.',
  },
  {
    t: 'p',
    x: 'That architectural longevity is not an accident. It is the difference between chasing regulatory change and absorbing it.',
  },
  {
    t: 'p',
    x: 'This moment also changes how AI is discussed internally. Enterprises can now say — credibly and simply — that regulators are asking them to prove how AI was constrained before it acted, and that Synthia is where those constraints live. Compliance teams stop being blockers and become allies. Security teams move from gatekeepers to integrators. Innovation teams move faster, not slower, because structure replaces ambiguity.',
  },
  {
    t: 'p',
    x: 'The subtle but important shift is this: before January 2026, governance platforms were defensive tools designed to reduce risk. After January 2026, governance is how AI is allowed to exist in regulated workflows at all.',
  },
  { t: 'p', x: 'Synthia was built for this reality.' },
]

const TIME_SAVED: Block[] = [
  {
    t: 'p',
    x: 'Here’s what the emerging evidence landscape is showing about why measuring real, audited outcomes — not just optimistic time-saved estimates — is becoming absolutely critical for investors and pilot sponsors.',
  },
  {
    t: 'p',
    x: 'Ambient AI clinical documentation studies are now moving past hype to quantify impact in real workflows. A large multicenter study published in JAMA Network Open found that using an ambient AI scribe across six health systems significantly reduced clinician burnout and documentation burden after 30 days — with clinicians reporting less after-hours charting, lighter cognitive load, and more face-to-face focus with patients, alongside measurable reductions in documentation time and other secondary outcomes. Early quality-improvement studies also link AI scribe use to reductions in burnout, administrative effort, and time documenting after hours.',
  },
  {
    t: 'p',
    x: 'At the same time, large productivity pilots like the UK government’s Microsoft 365 Copilot experiment show users reporting average daily time savings of about 26 minutes on routine office work — essentially adding up to weeks of work “freed up” over a year. But those time savings come with a major caveat: independent research from enterprise software provider Workday finds that about 37–40% of AI time savings are offset by the time workers spend reviewing, correcting, and rewriting AI-generated outputs — a real “AI tax” on productivity that erodes the net value if not explicitly measured.',
  },
  {
    t: 'p',
    x: 'This emerging pattern explains why simple gross time-saved estimates are insufficient for pilots that aim to influence investment decisions: claimed savings must be paired with metrics on corrected work and quality outcomes to reveal the true net benefit. That’s exactly the gap investors and government stakeholders are pushing to close — because pilots that only track reduction in documentation time can vastly overstate impact if they don’t also capture rework and compliance risk.',
  },
  {
    t: 'p',
    x: 'From a governance and audit perspective, this is why immutable evidence — backed by structured audit logs, versions, and approvals — matters: it lets sponsors prove that assistance was actually realized and not just lost to corrections or low-trust outputs. Early research highlights not just efficiency gains but the need for robust evaluation frameworks that measure both the productivity AI enables and the quality control effort it triggers.',
  },
  {
    t: 'p',
    x: 'In practice, this means building pilot measurement around three compact pillars: CFR (clinical failure rate) held steady; Assist-Minutes Saved versus true baseline work; and Compliance-Rework Avoided — not just time saved, but time not lost to fixing low-quality results. Anchoring these to an audit-ready evidence report with unforgeable linking from context → runtime → approval is what differentiates an investor-grade pilot from a superficial one.',
  },
  {
    t: 'p',
    x: 'The trend is clear: real evidence demands dual-track measurement — efficiency and corrected work — and early large-scale deployments are already validating both sides of that ledger.',
  },
]

const AGENTS_FAIL: Block[] = [
  {
    t: 'p',
    x: 'Context is not the enemy; it is the point. The whole thesis of our work — and of Synthia — is that the future belongs to systems that can deliver the right context to the right place at the right time. This is not a critique of context as a solution; it is a critique of autonomy without control. In a world where agent autonomy is accelerating, context alone is not enough to keep systems safe, predictable, and useful. That is why, even as analysts project that a large share of today’s agents will be abandoned within a few years, the real differentiator will not be who has the most context, but who has the most control over how that context is used.',
  },
  { t: 'h2', x: 'When Context Starts to Drift' },
  {
    t: 'p',
    x: 'In long-running AI conversations and workflows, there is a tipping point where a system stops acting like an instruction follower and starts acting like a pattern completer. As context piles up, the signal from the current instruction is diluted by the noise of everything that came before it. The model increasingly optimizes for “continue this trajectory coherently” instead of “do exactly what I just asked.”',
  },
  {
    t: 'p',
    x: 'This isn’t a random glitch. It’s a structural failure mode: context degradation. The more we treat the context window as a bottomless memory store, the more we force the system to reason on an overloaded, noisy workspace — and the more likely it is to drift away from intent.',
  },
  { t: 'h2', x: 'Context Windows Are Workspaces, Not Brains' },
  {
    t: 'p',
    x: 'A context window is a working surface, not a brain. It’s closer to a whiteboard than to long-term memory. Anything you want the model to use has to fit on that whiteboard in a form that is still legible under its attention and token limits.',
  },
  { t: 'p', x: 'That has three important implications:' },
  {
    t: 'ul',
    items: [
      'Long context is not free intelligence. More tokens mean more competition between relevant and irrelevant details.',
      'The model is not “learning” in a human sense mid-conversation; it is just re-conditioning on whatever is currently on the whiteboard.',
      'Without deliberate compression points — summaries, abstractions, resets — sessions slide from understanding into accumulation. Small misalignments early in the thread compound into big behavioral shifts later.',
    ],
  },
  {
    t: 'p',
    x: 'This is why “just give it more context” is not a strategy. Raw context without governance is how you end up with agents that feel smart at the start and unreliable at the critical moment.',
  },
  { t: 'h2', x: 'The Risk Gradient of Agent Autonomy' },
  {
    t: 'p',
    x: 'To understand why so many agents are on track to fail, it helps to map risk against what we actually let them do. You can think of four levels.',
  },
  { t: 'h2', x: 'Level 1: Deterministic Execution (Minimal Agentic Risk)' },
  {
    t: 'p',
    x: 'If X → Y. The input is known and the output is fixed. This is a function, not an agent. There’s essentially no agentic risk, because there is no discretion; the danger lies only in whether Y was the right rule to begin with.',
  },
  { t: 'h2', x: 'Level 2: Bounded Choice (Managed Risk)' },
  {
    t: 'p',
    x: 'If X → (A or B). The system chooses between predefined options. Risk appears when the selection logic is implicit or buried in prompt wording. Over time, the model can develop “preferences” based on what worked in past contexts, not what is correct right now. That’s how you get subtle preference drift.',
  },
  { t: 'h2', x: 'Level 3: Search and Retrieval (High Risk)' },
  {
    t: 'p',
    x: 'If X exists → find Y. Now the system is searching a large space — documents, tools, APIs — to decide what counts as “Y.” Y is not a single fixed answer; it is anything that passes a fuzzy set of criteria. This is where intent drift shows up: the system brings back Y′ that looks plausible, but doesn’t actually satisfy the real requirement.',
  },
  { t: 'h2', x: 'Level 4: Generative Construction (Very High Risk)' },
  {
    t: 'p',
    x: 'If X is ambiguous → construct Y from reasoning. Here the system is synthesizing Y from context, priors, and its own earlier outputs. That’s where you see confident, fluent, and wrong answers — especially as prior generations are fed back into context and treated as “facts.” This is recursive drift: every step that leans on earlier, imperfect outputs amplifies the error.',
  },
  {
    t: 'p',
    x: 'The point isn’t that Level 3 or 4 are “bad.” The point is that without control, risk doesn’t grow linearly with autonomy — it explodes at the intersection of autonomy, context saturation, and ambiguity.',
  },
  { t: 'h2', x: 'A Simple Way to Think About Agent Risk' },
  { t: 'p', x: 'You can capture this intuitively with a conceptual risk function:' },
  {
    t: 'ul',
    items: [
      'Autonomy (A): How much unsupervised decision-making is the agent allowed?',
      'Context Density (C): How saturated and noisy is the working context?',
      'Decision Ambiguity (D): How many plausible interpretations or valid outputs exist for a given X?',
    ],
  },
  { t: 'p', x: 'Conceptually: Risk grows roughly like A × C × D.' },
  {
    t: 'p',
    x: 'When all three are low, agents feel boring — but safe. When all three are high, small changes in any one (a slightly longer thread, a fuzzier instruction, a wider set of options) can cause outsized, unpredictable behavior.',
  },
  {
    t: 'p',
    x: 'This is why “more context” doesn’t automatically mean “better agents.” If you increase C without reducing A or D — or without governing how context is used — you are just giving the system more ways to go off course.',
  },
  { t: 'h2', x: 'When Governance Should Kick In' },
  {
    t: 'p',
    x: 'Not every workflow needs an agent, and not every agent needs heavy governance. Deterministic pipelines with clearly correct outputs don’t benefit from being wrapped in autonomous scaffolding. The question is: when should a control layer turn on?',
  },
  { t: 'p', x: 'You can think of a simple rule:' },
  {
    t: 'ul',
    items: [
      'Below a certain risk threshold, let the system behave like Level 1 or tightly constrained Level 2.',
      'Above that threshold, you must reduce effective autonomy, context exposure, and ambiguity before the agent is allowed to act.',
    ],
  },
  { t: 'p', x: 'In practice, that means:' },
  {
    t: 'ul',
    items: [
      'Don’t wrap pure functions in agent frameworks.',
      'Don’t let free-form agents operate directly on saturated context windows for high-stakes tasks.',
      'Do require that any agent operating in Level 3/4 capabilities is forced to behave like Level 1/2 at the decision boundary — through rules, constraints, validation, and escalation paths.',
    ],
  },
  {
    t: 'p',
    x: 'This is the gap most current “autonomous” agents fall into. They are given Level 3 and Level 4 powers with Level 0 governance.',
  },
  { t: 'h2', x: 'What Synthia Is Solving' },
  {
    t: 'p',
    x: 'This is where Synthia lives. Synthia is not about giving agents more context; it is about giving enterprises more control over how context is used.',
  },
  { t: 'p', x: 'At a high level, Synthia:' },
  {
    t: 'ul',
    items: [
      'Reduces effective autonomy by precompiling decision rules so agents execute logic instead of inventing it on the fly.',
      'Reduces effective context density by scoping retrieval and interaction to the slices of context that are relevant and permitted for the task at hand.',
      'Reduces effective ambiguity by defining valid outcomes, constraints, and escalation paths before the agent ever begins reasoning.',
    ],
  },
  {
    t: 'p',
    x: 'The net effect is simple: even when an agent feels like it is doing Level 3 search or Level 4 generation, its observable behavior is governed like a Level 1/2 system. It still uses powerful models and rich context, but it does so inside explicit, institutional logic — rather than improvising its own.',
  },
  {
    t: 'p',
    x: 'In a world where autonomy is racing ahead, that control layer is what will separate the agents that survive from the agents that quietly get turned off.',
  },
  { t: 'p', x: 'If this subject is of interest, here is a worthwhile read from our partner, Squirro:' },
  {
    t: 'link',
    href: 'https://www.linkedin.com/pulse/davos-2026-why-governance-security-real-enablers-agentic-ai-fjrje/',
    label: 'Davos 2026: Why governance and security are the real enablers of agentic AI',
  },
]

const ACCESS: Block[] = [
  {
    t: 'p',
    x: 'Modern AI has solved access. Models can retrieve information instantly, summarize vast corpora, and surface answers that would have taken hours to assemble just a few years ago. Yet despite this progress, enterprises remain dissatisfied with the results. Outputs feel generic. Responses require revision. High-stakes use cases still trigger hesitation.',
  },
  { t: 'p', x: 'The issue is not capability. It is judgment.' },
  {
    t: 'p',
    x: 'Access tells you what exists. Intelligence determines what applies, what matters, and what should be avoided in a given situation. Enterprises do not operate on information alone. They operate on standards, constraints, precedent, and experience. That logic is rarely written down cleanly, and it is almost never embedded automatically into AI interactions.',
  },
  {
    t: 'p',
    x: 'When organizations give employees access to powerful models without embedding judgment, they shift the burden of intelligence onto the human. The employee must remember compliance boundaries, brand expectations, role-specific constraints, situational risks, and organizational norms before even asking the question. If something is forgotten, the AI does not compensate. It produces a plausible answer that quietly violates expectations.',
  },
  { t: 'p', x: 'That is not intelligence. It is delegation without governance.' },
  { t: 'h2', x: 'Why access alone fails in enterprise environments' },
  {
    t: 'p',
    x: 'Enterprise success is not determined by how much information is available. It is determined by how consistently the right logic is applied. Teams know when to escalate, when to soften language, when to avoid speculation, and when silence is safer than speed. These decisions are contextual, not encyclopedic.',
  },
  {
    t: 'p',
    x: 'AI systems trained on broad data are excellent at recall. They are not designed to infer which rules apply in a specific organizational context unless those rules are explicitly activated. Without structure, models default to what is statistically likely, not what is institutionally correct.',
  },
  {
    t: 'p',
    x: 'This is why access-heavy AI strategies disappoint. More data does not equal better judgment. More retrieval does not equal safer decisions.',
  },
  { t: 'h2', x: 'The missing layer is instruction' },
  {
    t: 'p',
    x: 'The gap is not solved by better models or larger datasets. It is solved by introducing a layer that sits between human intent and machine generation. A layer that activates institutional knowledge before the model responds. A layer that remembers what humans forget under pressure.',
  },
  { t: 'p', x: 'This instruction layer does three things consistently:' },
  {
    t: 'ul',
    items: [
      'It embeds organizational standards automatically',
      'It reduces reliance on perfect prompting',
      'It enforces boundaries without slowing work down',
    ],
  },
  { t: 'p', x: 'When that layer exists, access becomes powerful. Without it, access becomes risky.' },
  { t: 'h2', x: 'Why training humans is not enough' },
  {
    t: 'p',
    x: 'Many organizations respond to inconsistent AI output by training employees to write better prompts. That approach does not scale. Humans are variable by nature. Even experts forget constraints when moving quickly. AI does not correct for those lapses. It amplifies them.',
  },
  { t: 'p', x: 'Intelligent systems do not rely on memory. They rely on structure.' },
  {
    t: 'p',
    x: 'Enterprises that succeed with AI will not be the ones with the most access. They will be the ones that capture their judgment and apply it consistently across every interaction, regardless of who is asking the question or which model is answering it.',
  },
  { t: 'p', x: 'Access opens the door. Intelligence decides what happens next.' },
]

const EIGHT_OF_TEN: Block[] = [
  { t: 'p', x: 'In consumer AI, an 80 percent success rate feels impressive. In the enterprise, it is unacceptable.' },
  {
    t: 'p',
    x: 'Eight out of ten good answers means two silent failures. Two responses that sound reasonable, read well, and still introduce risk. Two moments where something slightly wrong slips into a workflow that assumes the system is dependable.',
  },
  { t: 'p', x: 'Enterprise work is not graded on averages. It is graded on exceptions.' },
  { t: 'h2', x: 'Why probabilistic success breaks trust' },
  {
    t: 'p',
    x: 'AI systems are probabilistic by design. They generate what is likely, not what is required. That distinction is manageable in casual use. In professional environments, it becomes dangerous.',
  },
  {
    t: 'p',
    x: 'One misaligned sentence can trigger regulatory exposure. One speculative claim can create reputational risk. One tone error can undermine trust in a sensitive situation. These are not edge cases. They are the cost of operating without constraints.',
  },
  {
    t: 'p',
    x: 'When teams deploy AI without controlling how reasoning happens, they inherit that probabilistic behavior directly into their workflows. The result is familiar: revision loops, manual reviews, and quiet anxiety about what might slip through next.',
  },
  { t: 'p', x: 'Almost right is expensive.' },
  { t: 'h2', x: 'Why prompting is the wrong fix' },
  {
    t: 'p',
    x: 'When outputs fail, the instinct is to blame the prompt. If the answer was wrong, the prompt must have been incomplete. So employees are asked to be more explicit, more careful, more detailed.',
  },
  {
    t: 'p',
    x: 'This approach breaks down quickly. Prompt quality varies by person, experience, time pressure, and context. Even skilled users forget to include constraints. AI does not compensate for that variability. It assumes the absence of instruction is permission.',
  },
  { t: 'p', x: 'The enterprise does not need better prompts. It needs better guarantees.' },
  { t: 'h2', x: 'Consistency is what creates ROI' },
  {
    t: 'p',
    x: 'Trusted enterprise systems behave predictably. Not occasionally impressive, but reliably bounded. They reduce variance rather than showcasing creativity.',
  },
  {
    t: 'p',
    x: 'When institutional rules, role expectations, compliance logic, and brand standards are automatically activated before generation, the probability distribution changes. The model is no longer guessing in open space. It is reasoning inside defined boundaries.',
  },
  {
    t: 'p',
    x: 'At that point, success rates stop being a gamble. Revisions drop. Reviews accelerate. Confidence increases. Employees begin using AI for work that actually matters.',
  },
  { t: 'p', x: 'Eight out of ten is not a milestone. It is a warning sign.' },
]

const FRONTIER: Block[] = [
  {
    t: 'p',
    x: 'Every enterprise AI stack today is described in three layers: the model, the data, and the application. Most investment and attention goes to optimizing those components. But there is a fourth layer that determines whether any of it actually works at scale.',
  },
  { t: 'p', x: 'The instruction layer.' },
  {
    t: 'p',
    x: 'This is the layer that sits between your data and the model, encoding how your organization thinks before a single token is generated. It defines how reasoning should happen, not just what information is available. It captures judgment, constraints, priorities, and standards that are invisible to models unless they are made explicit.',
  },
  { t: 'p', x: 'This is where competitive advantage lives.' },
  {
    t: 'p',
    x: 'Models are becoming commodities. Every six months, a new frontier model appears that is more capable, faster, or cheaper than the last. No enterprise can sustain an advantage based on model selection alone. The same is increasingly true of data access. What persists across generations is not the engine, but the logic that governs how it is used.',
  },
  {
    t: 'p',
    x: 'The instruction layer is durable because it is model-agnostic. It works with today’s models and tomorrow’s replacements. It does not depend on how a model was trained. It depends on how your organization reasons.',
  },
  { t: 'h2', x: 'Why models cannot be the place logic lives' },
  {
    t: 'p',
    x: 'When logic is embedded implicitly inside a model’s behavior, it cannot be inspected, edited, or governed. When that logic changes, the enterprise has no control over why. This is unacceptable in environments where accountability, auditability, and consistency matter.',
  },
  {
    t: 'p',
    x: 'By contrast, an explicit instruction layer allows enterprises to define and manage:',
  },
  {
    t: 'ul',
    items: [
      'Reasoning patterns that reflect institutional judgment',
      'Compliance boundaries that must never be crossed',
      'Brand and tone standards that vary by context',
      'Decision frameworks that guide tradeoffs and restraint',
    ],
  },
  { t: 'p', x: 'This layer does not replace the model’s intelligence. It directs it.' },
  {
    t: 'p',
    x: 'The model generates language and explores possibilities. The instruction layer defines what is allowed, what is required, and what is forbidden before generation begins.',
  },
  { t: 'h2', x: 'Why context, not models, is the moat' },
  {
    t: 'p',
    x: 'As models converge in capability, differentiation shifts upward. The winners will not be those with the smartest models, but those with the most disciplined reasoning systems. Those who can consistently apply institutional intelligence across thousands of AI interactions, regardless of who is asking the question or which model is answering it.',
  },
  { t: 'p', x: 'This is why logic must live outside the model. Not for philosophical reasons, but for operational ones.' },
  {
    t: 'p',
    x: 'The instruction layer is where enterprises turn AI from a probabilistic tool into a dependable system. It is where context becomes enforceable. And it is where long-term advantage is built.',
  },
  { t: 'p', x: 'Models will change. Context should not.' },
]

const WHITE_PAPER: Block[] = [
  { t: 'h2', x: 'Executive Summary' },
  {
    t: 'p',
    x: 'Enterprises have invested billions in artificial intelligence infrastructure. Frontier models, data pipelines, vector search, retrieval-augmented generation, and agent frameworks are now standard components of modern AI stacks. And yet, the results remain inconsistent. Outputs require revision. Compliance teams remain uneasy. Employees hesitate to use AI for high-stakes work.',
  },
  { t: 'p', x: 'This failure is often misdiagnosed as a model problem or a data problem. In reality, it is neither.' },
  {
    t: 'p',
    x: 'Enterprise AI fails because it ignores the prompt layer — the human instruction layer where intent, judgment, and institutional context should be encoded before generation occurs. At the same time, it fails to capture the vast majority of enterprise intelligence that never appears in documents at all: tacit knowledge.',
  },
  {
    t: 'p',
    x: 'This paper explains why those two gaps are inseparable, and why the next wave of enterprise AI advantage will be built by organizations that solve both.',
  },
  { t: 'h2', x: 'Part I: Your AI Strategy Is Failing at the Prompt Layer' },
  { t: 'h2', x: 'The overlooked control point' },
  {
    t: 'p',
    x: 'Every enterprise AI system begins with a human asking a question. That moment is treated as trivial. In reality, it is the most consequential step in the entire workflow.',
  },
  {
    t: 'p',
    x: 'The prompt is not a technical detail. It is the interface between human reasoning and machine output. It determines what the model considers relevant, what constraints it applies, and what risks it is allowed to take.',
  },
  { t: 'p', x: 'Yet most AI strategies treat the prompt as an afterthought.' },
  {
    t: 'p',
    x: 'Enterprises focus on improving models, expanding data access, and refining retrieval systems. They assume intelligence will emerge automatically once enough information is available. What they discover instead is variability — answers that are sometimes excellent, sometimes flawed, and often unusable without human correction.',
  },
  { t: 'p', x: 'This inconsistency is not a failure of intelligence. It is a failure of governance.' },
  { t: 'h2', x: 'Why prompt engineering doesn’t scale' },
  {
    t: 'p',
    x: 'The standard response to poor AI output is to blame the prompt. If the answer was wrong, the instruction must have been incomplete. Employees are encouraged to be more specific, more detailed, and more careful.',
  },
  { t: 'p', x: 'This approach collapses at scale.' },
  {
    t: 'p',
    x: 'Prompt quality varies by individual skill, role, time pressure, and experience. Even expert users forget constraints. Even careful users omit assumptions. Models interpret silence as permission.',
  },
  {
    t: 'p',
    x: 'Asking humans to perfectly encode enterprise logic into every prompt is unrealistic. More importantly, it is unnecessary.',
  },
  { t: 'p', x: 'Governance systems exist to absorb human variability. Enterprise AI has largely ignored that principle.' },
  { t: 'h2', x: 'The cost of probabilistic reasoning in enterprise work' },
  {
    t: 'p',
    x: 'AI systems generate what is likely, not what is required. In consumer use, that distinction is tolerable. In enterprise environments, it becomes a liability.',
  },
  {
    t: 'p',
    x: 'One misaligned sentence can create regulatory exposure. One speculative claim can undermine trust. One tone error can damage a brand in a sensitive moment.',
  },
  { t: 'p', x: 'Enterprise systems are not judged on averages. They are judged on exceptions.' },
  {
    t: 'p',
    x: 'This is why many AI deployments plateau. Not because the tools lack power, but because leaders cannot trust them when stakes are high. Revision loops persist. Manual review remains mandatory. ROI remains elusive.',
  },
  { t: 'h2', x: 'The prompt layer as an instruction layer' },
  {
    t: 'p',
    x: 'The solution is not better prompting. It is removing the burden of governance from the individual prompt entirely.',
  },
  {
    t: 'p',
    x: 'Enterprises need an instruction layer that sits between human intent and machine generation. A layer that automatically injects institutional logic before the model responds.',
  },
  { t: 'p', x: 'This instruction layer encodes:' },
  {
    t: 'ul',
    items: [
      'Role-specific constraints and expectations',
      'Compliance and regulatory boundaries',
      'Brand voice and communication standards',
      'Risk tolerance and escalation rules',
    ],
  },
  {
    t: 'p',
    x: 'When these constraints are activated systematically, AI output becomes predictable. Variance decreases. Confidence increases. Employees stop fixing and start using.',
  },
  { t: 'p', x: 'The prompt layer is not a usability issue. It is the control plane of enterprise AI.' },
]

const EXEC_ABSTRACT: Block[] = [
  {
    t: 'p',
    x: 'Bridging the gap between intent and machine execution. Focus: enterprise governance, operational AI, and reasoning economics.',
  },
  { t: 'h2', x: 'Executive Abstract' },
  {
    t: 'p',
    x: 'Despite massive capital deployment, corporate adoption of generative AI has outpaced its economic returns. While 88% of organizations now use AI in at least one function, fewer than 6% qualify as “AI High Performers” with material EBIT impact [1]. This creates a “Reasoning Gap” — a divergence between model potential and business value.',
  },
  {
    t: 'p',
    x: 'This paper proposes a foundational shift in how enterprises architect AI: moving from Retrieval-Augmented Generation (RAG) to Contextually-Governed Reasoning (CGR). We introduce the concept of the “Instruction Layer,” a distinct architectural stratum that sits between intent and execution. By treating enterprise reasoning as a supply chain that requires industrial-grade quality controls, organizations can transition from probabilistic experimentation to deterministic, governed execution.',
  },
  { t: 'h2', x: '1. The Macro Problem: The Reasoning Defect' },
  {
    t: 'p',
    x: 'The prevailing assumption in enterprise AI is that better models yield better answers. This is mathematically incorrect in a corporate context. A model’s output is only as precise as its input instructions. Today, those instructions are fragile, relying on the variable prompting skills of individual employees.',
  },
  { t: 'p', x: 'This variance creates “Reasoning Defects” — errors in logic, tone, or compliance:' },
  {
    t: 'ul',
    items: [
      'The 95% failure reality: recent analyses indicate that nearly 95% of organizations report “no material ROI” from early generative investments, largely due to architectural failures rather than model intelligence [2].',
      'The “slop” crisis: a flood of ungoverned output creates “AI slop” — content that is generated, misused, or abused, creating risk and mistrust [3].',
      'The shadow AI risk: 93% of executive-level staff admit to using unapproved AI tools, creating a top-down governance vacuum [4].',
    ],
  },
  {
    t: 'p',
    x: 'The industry has solved for data storage (cloud) and data retrieval (RAG), but it has not solved for reasoning governance. The result is high friction, low trust, and paused initiatives.',
  },
  { t: 'h2', x: '2. Foundational Theory: The Access Fallacy' },
  {
    t: 'p',
    x: 'To understand why reasoning defects occur, we must confront the “Access Fallacy.” Most enterprise AI strategies focus on RAG — giving the model access to company data.',
  },
  { t: 'p', x: 'But access is not intelligence. As the adage goes:' },
  {
    t: 'p',
    x: '“You can give an intern the keys to the law library, but that doesn’t make them a lawyer.”',
  },
  {
    t: 'p',
    x: 'RAG gives the model the “keys to the library” (documents, files, data). It does not give the model the lawyer’s mind (judgment, risk tolerance, and strategic context). Without the latter, the model is merely a well-read intern — capable of citing facts, but dangerous when asked to reason.',
  },
  { t: 'h2', x: 'The Knowledge Iceberg' },
  { t: 'p', x: 'This distinction maps perfectly to the knowledge iceberg:' },
  {
    t: 'ul',
    items: [
      'Explicit knowledge (the visible 30%): this is the “library” — files, PDFs, and databases. RAG retrieves this.',
      'Tacit knowledge (the invisible 70%): this is the “judgment” — the unwritten rules, experience, competence, and political context that actually drive decisions.',
    ],
  },
  {
    t: 'p',
    x: 'The solution: the Instruction Layer functions as a tacit knowledge encoder. It explicitly injects the judgment (governance, role, context) into the equation, effectively lifting the invisible 70% of the iceberg and making it visible to the model.',
  },
  { t: 'h2', x: 'The Governing Equation' },
  { t: 'p', x: 'The traditional view of AI is input → output. The enterprise view must be:' },
  { t: 'p', x: 'Output = Model ( Library [30%] + Judgment [70%] )' },
  {
    t: 'p',
    x: 'Where “library” is intent plus data, and “judgment” is governance plus role plus context.',
  },
  {
    t: 'p',
    x: 'In this equation, the Instruction Layer solves for the missing variables that a raw model cannot know. By solving this equation before the model reasons, we change the nature of the interaction from a probabilistic query to a governed procedure.',
  },
  { t: 'h2', x: '3. The Architecture of Control' },
  {
    t: 'p',
    x: 'To operationalize the governing equation, the enterprise requires two distinct mechanisms: the Context Management System (CMS) and the Model Context Protocol (MCP).',
  },
  { t: 'h2', x: 'Why “System Prompts” Are Not Governance' },
  {
    t: 'p',
    x: 'A common misconception is that models can be governed simply by writing a system prompt — a block of text telling the model how to behave. This is insufficient for the enterprise because system prompts are probabilistic, not deterministic.',
  },
  {
    t: 'ul',
    items: [
      'The token problem: to a model, a system prompt is just a collection of tokens. Under pressure, models suffer from “instruction drift,” where they forget the system instructions in favor of user inputs [5].',
      'The security flaw: system prompts are vulnerable to jailbreaks. Because the instruction is inside the model’s context, it can be manipulated by the user [6].',
    ],
  },
  { t: 'h2', x: 'The Instruction Layer Solution' },
  {
    t: 'p',
    x: 'The Instruction Layer functions as middleware. It does not merely suggest behavior to the model; it enforces it via pre-flight interception.',
  },
  {
    t: 'ul',
    items: [
      'Intercept: it captures the raw request.',
      'Authenticate: it verifies identity and role.',
      'Inject: it retrieves the correct judgment logic (governance plus context) from the CMS.',
      'Execute: it constructs the governed instruction for the model.',
    ],
  },
  { t: 'h2', x: '4. Strategic Imperative: The Trap of Model Dependency' },
  {
    t: 'p',
    x: 'A critical, often overlooked risk is the commoditization trap. Frontier models are rapidly becoming interchangeable utilities.',
  },
  {
    t: 'ul',
    items: [
      'The danger of handing off reasoning: when an enterprise relies solely on a model’s native reasoning, it locks its business logic into that vendor’s ecosystem. This creates “prompt debt” — where complex prompt engineering works for one model but fails when ported to another [7].',
      'The threat of model drift: models change. Updates to weights or safety filters can silently alter how a model reasons, causing model drift that breaks established workflows [8].',
    ],
  },
  { t: 'h2', x: 'The Solution: Owning the Instruction Layer as IP' },
  {
    t: 'p',
    x: 'The enterprise must treat its reasoning logic — its corporate genome — as proprietary intellectual property. By abstracting this logic into an independent Instruction Layer, the enterprise achieves model independence. The Instruction Layer becomes a universal translator, dynamically formatting the enterprise’s rules for any model (OpenAI, Anthropic, Gemini, and others).',
  },
  { t: 'h2', x: '5. Defining the Category: Contextually-Governed Reasoning' },
  {
    t: 'p',
    x: 'As the market matures, governance is shifting from a passive audit function to an active operational layer.',
  },
  {
    t: 'p',
    x: 'Industry definition — “governed reasoning”: the broad aspiration to ensure AI outputs align with safety guidelines and policy, typically achieved through post-hoc review or static safety filters.',
  },
  {
    t: 'p',
    x: 'Synthia definition — “Contextually-Governed Reasoning (CGR)”: the specific architectural discipline of injecting identity, role, and policy context before the model executes. CGR is deterministic, pre-flight, and model-agnostic.',
  },
  { t: 'p', x: 'Synthia does not just monitor reasoning; it engineers it.' },
  { t: 'h2', x: '6. The Economics of Governed Reasoning' },
  {
    t: 'p',
    x: 'Transitioning from open reasoning to governed reasoning yields measurable improvements in yield and cycle time [9].',
  },
  {
    t: 'ul',
    items: [
      'First-pass usability: increases from 35% (uncontrolled) to 90% (governed).',
      'Compliance review time: drops from 9 days to 2 days (−78%).',
      'Regulatory rejections: drop from 38% to under 5% (−87%).',
      'Employee rework hours: reduce from 100 to 20 (−80%).',
      'Payback period: improves from over a year to under 30 days.',
    ],
  },
  {
    t: 'p',
    x: 'By reducing the reasoning defect rate, enterprises unlock the trapped value of AI. A communications department of 500 users can save an estimated $100k–$200k monthly simply by eliminating rework loops.',
  },
  { t: 'h2', x: 'Conclusion: The System of Record for Thinking' },
  {
    t: 'p',
    x: 'The next era of enterprise AI will not be defined by who has the smartest model, but by who has the cleanest reasoning supply chain.',
  },
  {
    t: 'p',
    x: 'The Instruction Layer is the missing piece of the enterprise stack. It bridges the gap between intent and execution, transforming AI from a stochastic experiment into a deterministic business system. For the enterprise, the mandate is clear: govern the reasoning, and you govern the result.',
  },
  { t: 'h2', x: 'References' },
  {
    t: 'ul',
    items: [
      'McKinsey & Company. “State of AI 2025: Agents, Innovation, and Transformation.” (November 2025).',
      'MIT Sloan Management Review & BCG. “The State of Generative AI ROI.” (2025).',
      'Synthia Internal Benchmarks. “Enterprise AI Defect Analysis: The Cost of ‘Slop’.” (2025).',
      'CyberNews. “Executive AI Use Survey 2025.” (2025).',
      'Reddit (r/PromptEngineering). “How Prompt Drift is Killing Enterprise AI Projects.” (2025).',
      'OpenAI Guardrails. “Protecting AI Applications from Prompt Attacks.” (2025).',
      'DEV Community. “The problem plaguing LLMOps: Prompt and Vendor lock-ins.” (March 2024).',
      'Lumenova AI. “Model Drift: Types, Causes and Early Detection.” (2025).',
      'Sand Hill East & Synthia. “ROI Impact Study: Contextually-Governed Reasoning in Financial Services.” (2025).',
    ],
  },
]

const CONTEXT_GRAPHS: Block[] = [
  {
    t: 'p',
    x: 'Most enterprise AI strategies focus on feeding models explicit knowledge — documents, policies, procedures. But that’s only 30% of what makes your organization effective. The other 70% is tacit knowledge: the judgment calls, the “we don’t do it that way,” the institutional wisdom that drives real decisions.',
  },
  {
    t: 'p',
    x: 'In this article, Foundation Capital explores why context engineering — the ability to encode tacit knowledge into AI systems — represents a trillion-dollar opportunity. It’s the difference between AI that knows your data and AI that thinks like your organization.',
  },
  {
    t: 'p',
    x: 'This is the infrastructure Synthia builds. We don’t just help AI access your information — we encode the reasoning patterns that turn information into institutional judgment.',
  },
  {
    t: 'link',
    href: 'https://foundationcapital.com/ideas/context-graphs-ais-trillion-dollar-opportunity',
    label: 'Read the full article at Foundation Capital',
  },
]

/** Slugs match the existing site exactly, so every current URL keeps working. */
export const POSTS: Post[] = [
  {
    slug: 'what-do-you-whisper-to-the-machine',
    title: 'What Do You Whisper to the Machine?',
    date: '2026-02-03',
    dateLabel: 'February 3, 2026',
    author: 'Drew Rayman',
    category: 'Pre-Flight',
    hero: HERO_WHISPER,
    card: CARD_WHISPER,
    excerpt:
      'Before an AI system answers a question, it sits idle. Cold. Dark. Silent. That instant — before the first token is produced — is where outcomes are decided, and it is the moment most enterprises leave to chance.',
    body: WHISPER,
  },
  {
    slug: 'time-saved-wrong-metric-ai-roi',
    title: 'Why “Time Saved” Is the Wrong Metric for AI ROI',
    date: '2026-02-02',
    dateLabel: 'February 2, 2026',
    author: 'Drew Rayman',
    category: 'Economics',
    hero: HERO_TIME,
    excerpt:
      'Roughly 37–40% of AI time savings are offset by reviewing, correcting, and rewriting what the model produced. Gross time saved hides the tax; net value only shows up when you measure the rework too.',
    body: TIME_SAVED,
  },
  {
    slug: 'january-2026-changed-rules-enterprise-ai',
    title: 'January 2026 Changed the Rules for Enterprise AI',
    date: '2026-01-27',
    dateLabel: 'January 27, 2026',
    author: 'Drew Rayman',
    category: 'Governance',
    hero: HERO_JANUARY,
    excerpt:
      'Regulators made something unmistakably clear: the next phase of AI adoption is not about better models or more impressive demos. It is about governance that exists before AI acts.',
    body: JANUARY,
  },
  {
    slug: 'why-agents-fail',
    title: 'Why Agents Fail',
    date: '2026-01-15',
    dateLabel: 'January 15, 2026',
    author: 'Drew Rayman',
    category: 'Agentic Systems',
    hero: HERO_AGENTS,
    excerpt:
      'Context is not the enemy; it is the point. This is not a critique of context as a solution — it is a critique of autonomy without control. The differentiator is not who has the most context, but who controls how it is used.',
    body: AGENTS_FAIL,
  },
  {
    slug: 'model-independence-context-outlives-llm',
    title: 'Why Frontier Models Should Not Own Your Logic',
    date: '2026-01-05',
    dateLabel: 'January 5, 2026',
    author: 'Neil Chinai',
    category: 'Instruction Layer',
    hero: HERO_FRONTIER,
    excerpt:
      'Models are becoming commodities. What persists across generations is not the engine, but the logic that governs how it is used. Models will change. Context should not.',
    body: FRONTIER,
  },
  {
    slug: 'access-not-intelligence',
    title: 'Access Is Not Intelligence',
    date: '2026-01-03',
    dateLabel: 'January 3, 2026',
    author: 'Drew Rayman',
    category: 'Contextual Intelligence',
    hero: HERO_ACCESS,
    excerpt:
      'Modern AI has solved access. Access tells you what exists; intelligence determines what applies, what matters, and what should be avoided. The issue is not capability. It is judgment.',
    body: ACCESS,
  },
  {
    slug: 'eight-of-ten-good-answers-liability',
    title: 'With AI, 8 out of 10 Good Answers Is a Liability',
    date: '2026-01-02',
    dateLabel: 'January 2, 2026',
    author: 'Drew Rayman',
    category: 'Enterprise AI',
    hero: HERO_EIGHT,
    excerpt:
      'Eight out of ten good answers means two silent failures — two responses that sound reasonable, read well, and still introduce risk. Enterprise work is not graded on averages. It is graded on exceptions.',
    body: EIGHT_OF_TEN,
  },
  {
    slug: 'tacit-knowledge-not-capturing',
    title: 'AI’s Trillion-Dollar Opportunity: Context Graphs',
    date: '2025-12-22',
    dateLabel: 'December 22, 2025',
    author: 'Jaya Gupta, Ashu Garg',
    category: 'Perspectives',
    hero: HERO_GRAPHS,
    excerpt:
      'Explicit knowledge is only 30% of what makes your organization effective. The other 70% is tacit: the judgment calls, the “we don’t do it that way,” the institutional wisdom that drives real decisions.',
    body: CONTEXT_GRAPHS,
  },
  {
    slug: 'instruction-layer-missing-infrastructure',
    title: 'Executive Abstract: The Instruction Layer',
    date: '2025-12-20',
    dateLabel: 'December 20, 2025',
    author: 'Mike Mazar',
    category: 'Executive Abstract',
    hero: HERO_EXEC,
    excerpt:
      'Moving from Retrieval-Augmented Generation to Contextually-Governed Reasoning — treating enterprise reasoning as a supply chain that requires industrial-grade quality controls.',
    body: EXEC_ABSTRACT,
  },
  {
    slug: 'why-enterprise-ai-fails-advantage',
    title: 'Why Enterprise AI Fails — and Where the Advantage Actually Lives',
    date: '2024-12-28',
    dateLabel: 'December 28, 2024',
    author: 'Alex Ruiz',
    category: 'White Paper',
    hero: HERO_PAPER,
    excerpt:
      'Enterprise AI fails because it ignores the prompt layer — where intent, judgment, and institutional context should be encoded before generation occurs. It is a failure of governance, not intelligence.',
    body: WHITE_PAPER,
  },
]

export const PUBLISHED = POSTS.filter((p) => !p.draft)

export const POSTS_PER_PAGE = 6

export function findPost(slug: string): Post | undefined {
  return POSTS.find((p) => p.slug === slug)
}
